import { useCallback, useContext, useRef } from "react";
import { flushSync } from "react-dom";

import {
  DIALOG,
  useDialogState,
} from "@/components/app/dialogs/dialog-provider";
import { useLocalEntryDialog } from "@/components/app/dialogs/use-local-entry-dialog";
import { EditorActionAudio } from "@/components/app/editor-v3/actions/editor-action-audio";
import { EditorActionDraft } from "@/components/app/editor-v3/actions/editor-action-draft";
import { EditorActionEmbed } from "@/components/app/editor-v3/actions/editor-action-embed";
import { EditorActionEmoji } from "@/components/app/editor-v3/actions/editor-action-emoji";
import { EditorActionGated } from "@/components/app/editor-v3/actions/editor-action-gated";
import { EditorActionGif } from "@/components/app/editor-v3/actions/editor-action-gif";
import { EditorActionPoll } from "@/components/app/editor-v3/actions/editor-action-poll";
import { EditorActionSubmit } from "@/components/app/editor-v3/actions/editor-action-submit";
import { EditorActionSwapGatedContent } from "@/components/app/editor-v3/actions/editor-action-swap-gated-content";
import { EditorActionVideo } from "@/components/app/editor-v3/actions/editor-action-video";
import {
  EDITOR_MEDIA_TAB,
  EDITOR_MODE,
} from "@/components/app/editor-v3/constants";
import {
  focusEditorEditableElement,
  focusIOSKeyboardBridge,
  isIOSDocument,
} from "@/components/app/editor-v3/editor-dom-focus";
import { EditorEmbedActionDetails } from "@/components/app/editor-v3/editor-embed-action-details";
import { EditorEmojiPicker } from "@/components/app/editor-v3/editor-emoji-picker";
import { EditorPollForm } from "@/components/app/editor-v3/editor-poll-form";
import { PostOverlayEditor } from "@/components/app/editor-v3/editors/post-overlay-editor";
import { EditorToolbar } from "@/components/app/editor-v3/toolbar";
import DesoAvatar from "@/components/shared/deso-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { useLayout } from "@/contexts/layout-context";
import { UserContext } from "@/contexts/user-context";
import { useMobile } from "@/hooks/use-mobile";
import { formatUSDCents } from "@/utils/currency";
import { AccessGroupType } from "@/utils/subscriptions";

import { EditorActionFormatting } from "../actions/editor-action-formatting";
import { EditorActionPhoto } from "../actions/editor-action-photo";
import { saveDraft } from "../draft-utils";
import {
  EditorFooterActionButton,
  EditorFooterActionContainer,
} from "../editor-action-utils";
import {
  EditorFieldContextProvider,
  useEditorContext,
} from "../editor-context";
import { EditorFileDropArea } from "../editor-file-drop-area";
import { EditorMediaContainer } from "../editor-media-container";
import { getEditorMediaUploadField } from "../editor-media-target";
import { TheEditor } from "../the-editor";

type PostEditorProps = {
  placeholder?: string;
  submitTitle?: string;
  autoOverlayEditor?: boolean;
};

type DialogOpenChangeDetails = {
  cancel?: () => void;
  event?: Event;
};

const isEditorChildOverlayTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  Boolean(target.closest("[data-vaul-drawer], [data-vaul-overlay]"));

export const PostEditor = ({
  placeholder = "What's on your mind?",
  submitTitle = "Post",
  autoOverlayEditor = false,
}: PostEditorProps = {}) => {
  const {
    body,
    gatedBody,
    media,
    gatedMedia,
    poll,
    mode,
    activeMediaTab,
    subscriptionTier,
    lastFocusedField,
    empty,
    exposed,
    getDataAttributes,
  } = useEditorContext();
  const { user } = useContext(UserContext);
  const firstPostPopoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const overlayEditorRootRef = useRef<HTMLDivElement | null>(null);
  const { confirm } = useConfirm();
  const confirmClose = useCallback(async () => {
    if (empty()) {
      return true;
    }

    return Boolean(
      await confirm({
        description:
          "You have unsaved content. Are you sure you want to discard?",
        type: "warning",
      }),
    );
  }, [confirm, empty]);
  const { open: overlayOpen, setOpen: setOverlayOpen } = useLocalEntryDialog(
    DIALOG.PostOverlayEditor,
    {
      beforeClose: confirmClose,
    },
  );
  const draftManagerDialogState = useDialogState(DIALOG.DraftManager);
  const gatedContentOptionsDialogState = useDialogState(
    DIALOG.GatedContentOptions,
  );
  const gifSearchDialogState = useDialogState(DIALOG.GifSearch);
  const insertImageDialogState = useDialogState(DIALOG.EditorInsertImage);
  const insertLinkDialogState = useDialogState(DIALOG.EditorInsertLink);
  const editorChildDialogOpen = [
    draftManagerDialogState,
    gatedContentOptionsDialogState,
    gifSearchDialogState,
    insertImageDialogState,
    insertLinkDialogState,
  ].some((dialogState) => dialogState?.open);
  const { isMobile } = useMobile();
  const { firstPostPopup, setFirstPostPopup } = useLayout();
  const mediaUploadField = getEditorMediaUploadField({
    isGated: Boolean(subscriptionTier),
    lastFocusedField,
  });
  const shouldIgnoreOverlayClose = useCallback(
    (eventDetails?: DialogOpenChangeDetails) => {
      if (editorChildDialogOpen) {
        return true;
      }

      return isEditorChildOverlayTarget(eventDetails?.event?.target ?? null);
    },
    [editorChildDialogOpen],
  );

  const focusOverlayEditor = useCallback(() => {
    const overlayRoot = overlayEditorRootRef.current;

    if (!overlayRoot) {
      return false;
    }

    return focusEditorEditableElement(overlayRoot, {
      dispatchClick: true,
      preventScroll: false,
    });
  }, []);

  const openOverlayEditor = useCallback(() => {
    if (isIOSDocument()) {
      focusIOSKeyboardBridge();
    }

    flushSync(() => {
      setOverlayOpen(true);
    });

    focusOverlayEditor();
    window.setTimeout(focusOverlayEditor, 0);
  }, [focusOverlayEditor, setOverlayOpen]);

  if (autoOverlayEditor) {
    exposed.setOpen = setOverlayOpen;
  }

  return (
    <Popover open={firstPostPopup}>
      <div
        ref={firstPostPopoverAnchorRef}
        className="relative flex w-full flex-col"
      >
        {isMobile && autoOverlayEditor && (
          <div className="absolute inset-0 z-1" onClick={openOverlayEditor} />
        )}
        <EditorFieldContextProvider name="body" placeholder={placeholder}>
          <div className="relative flex">
            <div className="grow">
              <div
                className="
                    h-0 overflow-hidden opacity-0 transition-[opacity,height]
                    duration-500 will-change-auto
                    in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
                  "
              >
                <Badge variant="outline" className="mb-4 inline-flex">
                  Preview content
                </Badge>
              </div>
              <div className="flex grow">
                <div
                  className="
                      hidden w-auto overflow-hidden opacity-100
                      transition-[opacity,width] duration-500 will-change-auto
                      in-data-[empty=false]:w-0 in-data-[empty=false]:opacity-0
                      in-data-[gated=true]:w-0 in-data-[gated=true]:opacity-0
                      md:block
                    "
                >
                  <DesoAvatar
                    size={32}
                    account={user}
                    className="mr-3"
                    clickable={false}
                  />
                </div>
                <div className="relative grow">
                  <div
                    className="
                        h-0 w-full overflow-hidden opacity-0
                        transition-[opacity,height] duration-500
                        will-change-auto
                        in-data-[empty=false]:h-auto
                        in-data-[empty=false]:opacity-100
                        in-data-[gated=true]:h-auto
                        in-data-[gated=true]:opacity-100
                        in-data-[mode=simple]:h-0
                      "
                  >
                    <EditorToolbar />
                  </div>
                  <TheEditor />
                  {autoOverlayEditor && (
                    <Dialog
                      open={overlayOpen}
                      onOpenChange={async (nextOpen, eventDetails) => {
                        if (nextOpen) {
                          setOverlayOpen(true);
                          return;
                        }

                        if (
                          shouldIgnoreOverlayClose(
                            eventDetails as DialogOpenChangeDetails | undefined,
                          )
                        ) {
                          eventDetails?.cancel();
                          return false;
                        }

                        if (!(await confirmClose())) {
                          return false;
                        }

                        setOverlayOpen(false);
                      }}
                    >
                      <DialogContent
                        bodyClassName="
                          unforce-radius rounded-none border-0 shadow-none
                        "
                        className="
                            content unforce-radius
                            h-(--overlay-keyboard-available-height)
                            max-h-(--overlay-keyboard-available-height) max-w-full
                            overflow-hidden rounded-none border-0
                            md:max-h-[90vh] md:max-w-[700px] md:rounded-lg
                          "
                        closeClassName="top-2 right-2 size-9 p-2"
                        onCloseAutoFocus={(event) => {
                          if (isIOSDocument()) {
                            event.preventDefault();
                          }
                        }}
                        onOpenAutoFocus={(event) => {
                          event.preventDefault();
                        }}
                        hideOverlay
                        {...getDataAttributes()}
                      >
                        <div ref={overlayEditorRootRef} className="contents">
                          <style>{`html.os-ios body > main { opacity:0 }`}</style>
                          <DialogTitle className="hidden">
                            Add a post
                          </DialogTitle>
                          <DialogDescription className="hidden">
                            Add Post
                          </DialogDescription>
                          <PostOverlayEditor />
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>

            <div
              className="
                  relative z-2 w-auto place-content-center overflow-hidden pl-2
                  opacity-100 transition-[opacity,width] duration-500
                  will-change-auto
                  in-data-[empty=false]:w-0 in-data-[empty=false]:opacity-0
                  in-data-[gated=true]:w-0 in-data-[gated=true]:opacity-0
                "
            >
              <div className="flex gap-2">
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorActionPhoto variant="ghost" className="h-auto p-1" />
                </EditorFieldContextProvider>
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorActionVideo variant="ghost" className="h-auto p-1" />
                </EditorFieldContextProvider>
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorActionAudio
                    variant="ghost"
                    className="h-auto p-1"
                    side="bottom"
                    align="end"
                  />
                </EditorFieldContextProvider>
                <EditorFieldContextProvider name={lastFocusedField || "body"}>
                  <EditorActionGif
                    variant="ghost"
                    className="
                        hidden h-auto p-1
                        md:inline-flex
                      "
                  />
                </EditorFieldContextProvider>
              </div>
            </div>
          </div>
          <EditorFieldContextProvider name="media">
            <EditorMediaContainer />
            <EditorFileDropArea
              targetLabel={subscriptionTier ? "preview content" : "this post"}
            />
          </EditorFieldContextProvider>
        </EditorFieldContextProvider>

        <div
          className="
              h-0 overflow-hidden opacity-0 transition-[opacity,height]
              duration-500 will-change-auto
              in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
            "
        >
          <EditorFieldContextProvider
            name="gatedBody"
            placeholder="Add your gated content here..."
          >
            <div className="relative my-4 flex items-center">
              <div className="h-px flex-1 bg-border" />
              <EditorActionSwapGatedContent
                className="
                  absolute left-1/2 size-8 -translate-x-1/2 bg-background
                "
              />
            </div>
            <div
              className="
                  h-0 overflow-hidden opacity-0 transition-[opacity,height]
                  duration-500 will-change-auto
                  in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
                "
            >
              <Badge variant="outline" className="mr-2 mb-4 inline-flex">
                Gated content
              </Badge>
              {subscriptionTier && (
                <Badge
                  variant="outline"
                  className="mb-4 inline-flex text-muted-foreground"
                >
                  {subscriptionTier!.accessGroupKeyName.includes(
                    AccessGroupType.OneTime,
                  )
                    ? `Pay Once to Unlock - ${formatUSDCents(subscriptionTier.costUsdCents)}`
                    : `${subscriptionTier.name} - ${subscriptionTier.costUsdCents === 0 ? "Free" : `${formatUSDCents(subscriptionTier.costUsdCents)}/mo`}`}
                </Badge>
              )}
            </div>
            <div className="relative">
              <div
                className="
                    h-0 w-full overflow-hidden opacity-0
                    transition-[opacity,height] duration-500 will-change-auto
                    in-data-[empty=false]:h-auto
                    in-data-[empty=false]:opacity-100
                    in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
                    in-data-[mode=simple]:h-0
                  "
              >
                <EditorToolbar />
              </div>
              <TheEditor />
              {autoOverlayEditor && (
                <div
                  className="
                      absolute inset-0 z-10
                      md:hidden
                    "
                  onClick={openOverlayEditor}
                />
              )}
            </div>
            <EditorFieldContextProvider name="gatedMedia">
              <EditorMediaContainer />
              <EditorFileDropArea targetLabel="gated content" />
            </EditorFieldContextProvider>
          </EditorFieldContextProvider>
        </div>

        <div
          className="
              h-0 overflow-hidden transition-[height] duration-500
              will-change-auto
              in-data-[media-tab=poll]:h-auto
              in-data-[media-tab=poll]:opacity-100
            "
        >
          <EditorFieldContextProvider name="poll">
            <div className="my-4 h-px bg-border" />
            <Badge variant="outline" className="mb-4 inline-flex">
              Poll content
            </Badge>
            <EditorPollForm />
          </EditorFieldContextProvider>
        </div>

        <div
          className="
              hidden
              in-data-[media-tab=emoji]:block
            "
        >
          <EditorFieldContextProvider name={lastFocusedField || "body"}>
            <div className="mt-4" />
            <EditorEmojiPicker />
          </EditorFieldContextProvider>
        </div>

        <div
          className="
              h-0 overflow-hidden transition-[height] duration-500
              will-change-auto
              in-data-[media-tab=embed]:h-auto
              in-data-[media-tab=embed]:opacity-100
            "
        >
          <div className="my-4 h-px bg-border" />
          <Badge variant="outline" className="mb-4 inline-flex">
            Embed content
          </Badge>
          <EditorEmbedActionDetails />
        </div>

        <div
          className="
              h-0 overflow-hidden opacity-0 transition-[opacity,height]
              duration-500 will-change-auto
              in-data-[empty=false]:h-auto in-data-[empty=false]:opacity-100
              in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
            "
        >
          <div className="mt-6 flex">
            <div className="grow">
              <EditorFooterActionContainer>
                <EditorFooterActionButton
                  action={EditorActionFormatting}
                  tooltipText="Formatting"
                  active={mode === EDITOR_MODE.RICH}
                />
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorFooterActionButton
                    action={EditorActionPhoto}
                    tooltipText="Upload Images"
                  />
                </EditorFieldContextProvider>
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorFooterActionButton
                    action={EditorActionVideo}
                    tooltipText="Upload Videos"
                  />
                </EditorFieldContextProvider>
                <EditorFieldContextProvider name={mediaUploadField}>
                  <EditorFooterActionButton
                    action={EditorActionAudio}
                    tooltipText="Upload Audio"
                    side="top"
                  />
                </EditorFieldContextProvider>
                <EditorFooterActionButton
                  action={EditorActionPoll}
                  tooltipText="Add Poll"
                  active={activeMediaTab === EDITOR_MEDIA_TAB.Poll}
                />
                <EditorFieldContextProvider name={lastFocusedField || "body"}>
                  <EditorFooterActionButton
                    action={EditorActionGif}
                    tooltipText="Add GIF"
                  />
                </EditorFieldContextProvider>
                <EditorFooterActionButton
                  action={EditorActionEmbed}
                  tooltipText="Manage embedded media"
                  active={activeMediaTab === EDITOR_MEDIA_TAB.Embed}
                />
                <EditorFieldContextProvider name={lastFocusedField || "body"}>
                  <EditorFooterActionButton
                    action={EditorActionEmoji}
                    tooltipText="Add emoji"
                    active={activeMediaTab === EDITOR_MEDIA_TAB.Emoji}
                  />
                </EditorFieldContextProvider>
              </EditorFooterActionContainer>
            </div>

            <div className="ml-auto flex gap-2">
              <div>
                <EditorActionDraft />
              </div>
              <div>
                <EditorActionGated />
              </div>
              <div>
                <EditorActionSubmit
                  title={submitTitle}
                  className="px-3"
                  onClick={() => {
                    saveDraft(
                      user!.publicKey,
                      {
                        body,
                        gatedBody,
                        media,
                        gatedMedia,
                        poll,
                      },
                      subscriptionTier,
                      true,
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PopoverContent
        align="end"
        anchor={firstPostPopoverAnchorRef}
        className="z-90 w-full max-w-[300px] border-primary!"
      >
        Make your first post and introduce yourself to the community!
        <br />
        <Button
          className="mt-4"
          variant="outline"
          size="sm"
          onClick={() => setFirstPostPopup(false)}
        >
          Got it!
        </Button>
      </PopoverContent>
    </Popover>
  );
};
